import Pagination from "@/components/Pagination";
import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Modal from "@/components/Modal";
import ConfirmDialog from "@/components/ConfirmDialog";
import { InlineError } from "@/components/InlineError";
import { Building2, Plus, Pencil, Trash2, LogOut, Users, Layers, ShieldCheck, Loader2, Mail, Phone, MapPin, Search, UserCog } from "lucide-react";

const CURRENCIES = ["KMF", "XOF", "XAF", "EUR", "USD"];
const emptyForm = { name: "", owner_first_name: "", owner_last_name: "", owner_email: "", owner_phone: "", owner_address: "", currency: "KMF" };

export default function PlatformAdmin() {
  const { user, logout } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [createErr, setCreateErr] = useState("");
  const [confirmCreate, setConfirmCreate] = useState(false);
  const [edit, setEdit] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editErr, setEditErr] = useState("");
  const [created, setCreated] = useState(null);
  const [delTenant, setDelTenant] = useState(null);
  const [delTenantLoading, setDelTenantLoading] = useState(false);
  const [delTenantErr, setDelTenantErr] = useState("");
  const [usersFor, setUsersFor] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [delUser, setDelUser] = useState(null);
  const [delUserLoading, setDelUserLoading] = useState(false);
  const [delUserErr, setDelUserErr] = useState("");
  // super admin account email change
  const [acct, setAcct] = useState(false);
  const [acctForm, setAcctForm] = useState({ new_email: "", current_password: "" });
  const [acctErr, setAcctErr] = useState("");
  const [acctBusy, setAcctBusy] = useState(false);
  const [acctOk, setAcctOk] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/superadmin/tenants?page=${page}&limit=6&q=${encodeURIComponent(q)}`);
      setTenants(data.items || []); setTotal(data.total || 0);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail, e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);
  // reset to page 1 on new search (debounced)
  useEffect(() => {
    const t = setTimeout(() => { if (page !== 1) setPage(1); else load(); }, 350);
    return () => clearTimeout(t); /* eslint-disable-next-line */
  }, [q]);

  const askCreate = () => {
    setCreateErr("");
    if (!form.name.trim() || !form.owner_email.trim()) { setCreateErr("Le nom de l'entreprise et l'e-mail du propriétaire sont obligatoires."); return; }
    setConfirmCreate(true);
  };
  const create = async () => {
    setSaving(true); setCreateErr("");
    try {
      const { data } = await api.post("/superadmin/tenants", form);
      toast.success("Entreprise créée");
      setConfirmCreate(false); setOpen(false); setForm(emptyForm); setCreated(data);
      if (page !== 1) setPage(1); else load();
    } catch (e) { setConfirmCreate(false); setCreateErr(apiErr(e.response?.data?.detail, e)); }
    finally { setSaving(false); }
  };

  const openEdit = (t) => {
    setEditErr(""); setEdit(t);
    setEditForm({ name: t.name, status: t.status || "active", currency: t.currency || "KMF",
      owner_first_name: t.owner_first_name || "", owner_last_name: t.owner_last_name || "",
      owner_email: t.owner_email || "", owner_phone: t.owner_phone || "", owner_address: t.owner_address || "" });
  };
  const saveEdit = async () => {
    setEditErr("");
    if (!editForm.name?.trim()) { setEditErr("Le nom est obligatoire."); return; }
    setSaving(true);
    try {
      await api.post(`/superadmin/tenants/${edit.id}`, editForm);
      toast.success("Entreprise et propriétaire mis à jour"); setEdit(null); load();
    } catch (e) { setEditErr(apiErr(e.response?.data?.detail, e)); }
    finally { setSaving(false); }
  };

  const resendInvite = async (t) => {
    try { const { data } = await api.post(`/superadmin/tenants/${t.id}/resend-invite`); toast.success(`Nouveau lien envoyé à ${data.email}`); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail, e)); }
  };

  const doDeleteTenant = async () => {
    setDelTenantLoading(true); setDelTenantErr("");
    try {
      await api.delete(`/superadmin/tenants/${delTenant.id}`);
      toast.success("Entreprise supprimée"); setDelTenant(null); load();
    } catch (e) { setDelTenantErr(apiErr(e.response?.data?.detail, e)); }
    finally { setDelTenantLoading(false); }
  };

  const openUsers = async (t) => {
    setUsersFor(t); setUsersLoading(true); setUsersList([]);
    try { const { data } = await api.get(`/superadmin/tenants/${t.id}/users`); setUsersList(data); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail, e)); }
    finally { setUsersLoading(false); }
  };
  const doDeleteUser = async () => {
    setDelUserLoading(true); setDelUserErr("");
    try {
      await api.delete(`/superadmin/tenants/${usersFor.id}/users/${delUser.id}`);
      toast.success("Utilisateur supprimé");
      setUsersList((l) => l.filter((x) => x.id !== delUser.id));
      setDelUser(null); load();
    } catch (e) { setDelUserErr(apiErr(e.response?.data?.detail, e)); }
    finally { setDelUserLoading(false); }
  };

  const submitEmailChange = async () => {
    setAcctErr(""); setAcctOk("");
    if (!acctForm.new_email.trim() || !acctForm.current_password) { setAcctErr("Renseignez la nouvelle adresse et votre mot de passe actuel."); return; }
    setAcctBusy(true);
    try {
      const { data } = await api.post("/superadmin/account/email/request", acctForm);
      setAcctOk(data.message || "Lien de vérification envoyé."); setAcctForm({ new_email: "", current_password: "" });
    } catch (e) { setAcctErr(apiErr(e.response?.data?.detail, e)); }
    finally { setAcctBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F4F6FB]">
      <header className="bg-gradient-to-r from-[#16324F] to-[#0E2135] text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2563EB] to-[#0E7490] flex items-center justify-center font-bold text-lg font-head">B</div>
            <div className="min-w-0">
              <div className="font-bold font-head leading-none flex items-center gap-2">Biashara <span className="text-[10px] font-semibold bg-white/15 px-2 py-0.5 rounded-full flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Console Super Admin</span></div>
              <div className="text-white/50 text-xs mt-0.5 truncate">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => { setAcctErr(""); setAcctOk(""); setAcctForm({ new_email: "", current_password: "" }); setAcct(true); }} className="flex items-center gap-2 text-sm bg-white/10 hover:bg-white/20 transition-colors px-3 py-2 rounded-lg" data-testid="sa-account-btn"><UserCog className="w-4 h-4" /> Mon compte</button>
            <button onClick={logout} className="flex items-center gap-2 text-sm bg-white/10 hover:bg-white/20 transition-colors px-3 py-2 rounded-lg" data-testid="sa-logout-btn"><LogOut className="w-4 h-4" /> Se déconnecter</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-head text-[#16324F] tracking-tight">Entreprises</h1>
            <p className="text-sm text-[#64748B] mt-1">Créez et gérez les entreprises clientes. Chaque entreprise est totalement isolée.</p>
          </div>
          <button onClick={() => { setForm(emptyForm); setCreateErr(""); setOpen(true); }} className="btn-primary" data-testid="sa-new-tenant-btn"><Plus className="w-4 h-4" /> Nouvelle entreprise</button>
        </div>

        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <input className="inp pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une entreprise…" data-testid="sa-search-input" />
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-[#64748B] py-16 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Chargement…</div>
        ) : tenants.length === 0 ? (
          <div className="card p-10 text-center" data-testid="sa-empty">
            <Building2 className="w-10 h-10 mx-auto text-[#94A3B8]" />
            <p className="mt-3 font-semibold text-[#16324F]">Aucune entreprise{q ? " pour cette recherche" : " pour le moment"}</p>
            <p className="text-sm text-[#64748B] mt-1">{q ? "Modifiez votre recherche." : "Créez la première entreprise cliente pour commencer."}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tenants.map((t) => (
              <div key={t.id} className="card card-hover p-5" data-testid={`sa-tenant-card-${t.id}`}>
                <div className="flex items-start justify-between">
                  <div className="w-11 h-11 rounded-lg bg-[#16324F]/5 text-[#16324F] flex items-center justify-center"><Building2 className="w-5 h-5" /></div>
                  <span className={t.status === "suspended" ? "badge-warning" : "badge-success"}>{t.status === "suspended" ? "Suspendue" : "Active"}</span>
                </div>
                <h3 className="mt-3 font-semibold font-head text-[#16324F]">{t.name}</h3>
                <div className="mt-2 space-y-1 text-xs text-[#475569]">
                  <p className="flex items-center gap-1.5" data-testid={`sa-owner-name-${t.id}`}><UserCog className="w-3.5 h-3.5 text-[#94A3B8]" /> {t.owner_name || "Propriétaire"}</p>
                  <p className="flex items-center gap-1.5 truncate" title={t.owner_email}><Mail className="w-3.5 h-3.5 text-[#94A3B8]" /> {t.owner_email || "—"}</p>
                  <p className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-[#94A3B8]" /> {t.owner_phone || "—"}</p>
                </div>
                <div className="flex items-center gap-4 text-xs text-[#475569] mt-3 pt-3 border-t border-[#F1F5F9]">
                  <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {t.users} util.</span>
                  <span className="flex items-center gap-1"><Layers className="w-3.5 h-3.5" /> {t.activities} activité(s)</span>
                  <span className="font-semibold text-[#2563EB] font-mono">{t.currency}</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <button onClick={() => openEdit(t)} className="btn-edit !py-1.5 !px-3" data-testid={`sa-edit-${t.id}`}><Pencil className="w-3.5 h-3.5" /> Modifier</button>
                  <button onClick={() => openUsers(t)} className="btn-outline !py-1.5 !px-3" data-testid={`sa-users-${t.id}`}><Users className="w-3.5 h-3.5" /> Utilisateurs</button>
                  <button onClick={() => resendInvite(t)} className="btn-outline !py-1.5 !px-3" title="Renvoyer le lien d'invitation au propriétaire" data-testid={`sa-resend-${t.id}`}><Mail className="w-3.5 h-3.5" /> Renvoyer le lien</button>
                  <button onClick={() => { setDelTenantErr(""); setDelTenant(t); }} className="btn-remove !py-1.5 !px-3" data-testid={`sa-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5" /> Supprimer</button>
                </div>
              </div>
            ))}
          </div>
        )}
        <Pagination page={page} setPage={setPage} total={total} />
      </main>

      {/* Nouvelle entreprise */}
      <Modal open={open} onClose={() => setOpen(false)} title="Nouvelle entreprise" wide testId="sa-tenant-modal"
        footer={<><button className="btn-outline" onClick={() => setOpen(false)}>Annuler</button><button className="btn-primary" onClick={askCreate} disabled={saving} data-testid="sa-save-tenant-btn"><Plus className="w-4 h-4" /> Créer l'entreprise</button></>}>
        <div className="space-y-4">
          <div><label className="lbl">Nom de l'entreprise</label><input className="inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sa-tenant-name-input" placeholder="Ex : Société Halidi" /></div>
          <div><label className="lbl">Devise</label>
            <select className="inp" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="sa-tenant-currency-input">
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="border-t border-[#E2E8F0] pt-3">
            <div className="font-semibold text-[#16324F] text-sm mb-2">Propriétaire</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className="lbl">Prénom</label><input className="inp" value={form.owner_first_name} onChange={(e) => setForm({ ...form, owner_first_name: e.target.value })} data-testid="sa-owner-firstname-input" /></div>
              <div><label className="lbl">Nom</label><input className="inp" value={form.owner_last_name} onChange={(e) => setForm({ ...form, owner_last_name: e.target.value })} data-testid="sa-owner-lastname-input" /></div>
            </div>
            <div className="mt-3"><label className="lbl">E-mail de connexion</label><input type="email" className="inp" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} data-testid="sa-tenant-owner-input" placeholder="proprietaire@exemple.com" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <div><label className="lbl">Téléphone</label><input className="inp" value={form.owner_phone} onChange={(e) => setForm({ ...form, owner_phone: e.target.value })} data-testid="sa-owner-phone-input" /></div>
              <div><label className="lbl">Adresse (facultatif)</label><input className="inp" value={form.owner_address} onChange={(e) => setForm({ ...form, owner_address: e.target.value })} data-testid="sa-owner-address-input" /></div>
            </div>
          </div>
          <p className="text-xs text-[#64748B] bg-[#EFF6FF] border border-[#bfdbfe] rounded-lg p-3">Ces coordonnées sont enregistrées sur le compte Propriétaire. Un e-mail d'invitation lui sera envoyé pour définir son mot de passe.</p>
          <InlineError message={createErr} title="Création impossible" />
        </div>
      </Modal>

      <ConfirmDialog open={confirmCreate} danger={false} title="Confirmer la création"
        message={`Créer l'entreprise « ${form.name} » et inviter ${form.owner_email} comme propriétaire ?`}
        confirmLabel="Valider et créer" loading={saving} onConfirm={create} onClose={() => setConfirmCreate(false)} />

      {/* Modifier entreprise + propriétaire */}
      <Modal open={!!edit} onClose={() => setEdit(null)} title={`Modifier — ${edit?.name || ""}`} wide testId="sa-tenant-edit-modal"
        footer={<><button className="btn-outline" onClick={() => setEdit(null)}>Annuler</button><button className="btn-primary" onClick={saveEdit} disabled={saving} data-testid="sa-save-edit-btn">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer</button></>}>
        <div className="space-y-4">
          <div><label className="lbl">Nom de l'entreprise</label><input className="inp" value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="sa-edit-name-input" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className="lbl">Statut</label>
              <select className="inp" value={editForm.status || "active"} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} data-testid="sa-edit-status-input">
                <option value="active">Active</option>
                <option value="suspended">Suspendue</option>
              </select>
            </div>
            <div><label className="lbl">Devise</label>
              <select className="inp" value={editForm.currency || "KMF"} onChange={(e) => setEditForm({ ...editForm, currency: e.target.value })} data-testid="sa-edit-currency-input">
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="border-t border-[#E2E8F0] pt-3">
            <div className="font-semibold text-[#16324F] text-sm mb-2">Coordonnées du propriétaire</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className="lbl">Prénom</label><input className="inp" value={editForm.owner_first_name || ""} onChange={(e) => setEditForm({ ...editForm, owner_first_name: e.target.value })} data-testid="sa-edit-owner-firstname" /></div>
              <div><label className="lbl">Nom</label><input className="inp" value={editForm.owner_last_name || ""} onChange={(e) => setEditForm({ ...editForm, owner_last_name: e.target.value })} data-testid="sa-edit-owner-lastname" /></div>
            </div>
            <div className="mt-3"><label className="lbl">E-mail de connexion</label><input type="email" className="inp" value={editForm.owner_email || ""} onChange={(e) => setEditForm({ ...editForm, owner_email: e.target.value })} data-testid="sa-edit-owner-email" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <div><label className="lbl">Téléphone</label><input className="inp" value={editForm.owner_phone || ""} onChange={(e) => setEditForm({ ...editForm, owner_phone: e.target.value })} data-testid="sa-edit-owner-phone" /></div>
              <div><label className="lbl">Adresse</label><input className="inp" value={editForm.owner_address || ""} onChange={(e) => setEditForm({ ...editForm, owner_address: e.target.value })} data-testid="sa-edit-owner-address" /></div>
            </div>
            <p className="text-xs text-[#64748B] mt-2">La modification met à jour directement le compte Propriétaire. Les anciens comptes sans coordonnées continuent de fonctionner.</p>
          </div>
          <InlineError message={editErr} title="Mise à jour impossible" />
        </div>
      </Modal>

      <Modal open={!!created} onClose={() => setCreated(null)} title="Entreprise créée" testId="sa-created-modal"
        footer={<button className="btn-primary" onClick={() => setCreated(null)} data-testid="sa-created-ok">Compris</button>}>
        <div className="space-y-3 text-sm text-[#334155]">
          <p><b>{created?.name}</b> a été créée avec le propriétaire <b>{created?.owner_email}</b>.</p>
          <p className="bg-[#EFF6FF] border border-[#bfdbfe] rounded-lg p-3">Un e-mail d'invitation a été envoyé à <b>{created?.owner_email}</b> avec un lien pour définir son mot de passe. S'il ne le reçoit pas, il peut utiliser « Mot de passe oublié » sur l'écran de connexion.</p>
        </div>
      </Modal>

      {/* Mon compte — changement d'e-mail Super Admin */}
      <Modal open={acct} onClose={() => setAcct(false)} title="Mon compte Super Admin" testId="sa-account-modal"
        footer={<><button className="btn-outline" onClick={() => setAcct(false)}>Fermer</button><button className="btn-primary" onClick={submitEmailChange} disabled={acctBusy} data-testid="sa-account-submit">{acctBusy && <Loader2 className="w-4 h-4 animate-spin" />} Envoyer le lien de vérification</button></>}>
        <div className="space-y-3">
          <p className="text-sm text-[#475569]">Adresse actuelle : <b>{user?.email}</b></p>
          <div><label className="lbl">Nouvelle adresse e-mail</label><input type="email" className="inp" value={acctForm.new_email} onChange={(e) => setAcctForm({ ...acctForm, new_email: e.target.value })} data-testid="sa-new-email-input" placeholder="nouvelle@exemple.com" /></div>
          <div><label className="lbl">Mot de passe actuel</label><input type="password" className="inp" value={acctForm.current_password} onChange={(e) => setAcctForm({ ...acctForm, current_password: e.target.value })} data-testid="sa-current-password-input" autoComplete="current-password" /></div>
          <p className="text-xs text-[#64748B] bg-[#EFF6FF] border border-[#bfdbfe] rounded-lg p-3">Un lien de confirmation sera envoyé à la nouvelle adresse. L'adresse actuelle reste active tant que la nouvelle n'est pas confirmée. Aucun compte entreprise n'est affecté.</p>
          {acctOk && <div className="text-sm text-[#15803D] bg-[#DCFCE7] rounded-lg p-3" data-testid="sa-account-ok">{acctOk}</div>}
          <InlineError message={acctErr} title="Action impossible" />
        </div>
      </Modal>

      <Modal open={!!usersFor} onClose={() => setUsersFor(null)} title={`Utilisateurs — ${usersFor?.name || ""}`} testId="sa-users-modal"
        footer={<button className="btn-outline" onClick={() => setUsersFor(null)}>Fermer</button>}>
        {usersLoading ? (
          <div className="flex items-center gap-2 text-[#64748B] py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
        ) : usersList.length === 0 ? (
          <p className="text-sm text-[#64748B] py-6 text-center">Aucun utilisateur.</p>
        ) : (
          <div className="space-y-2" data-testid="sa-users-list">
            {usersList.map((u) => (
              <div key={u.id} className="flex items-center justify-between gap-3 border border-[#E7ECF3] rounded-xl p-3 hover:bg-[#F8FAFC] transition-colors" data-testid={`sa-user-row-${u.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-[#16324F] text-sm truncate flex items-center gap-2">{u.name || u.email}{u.is_owner && <span className="text-[10px] bg-[#EFF6FF] text-[#2563EB] px-1.5 py-0.5 rounded-full">Propriétaire</span>}</div>
                  <div className="text-xs text-[#64748B] truncate">{u.email} · {u.role} · <span className="font-mono">{u.sales_count}</span> vente(s)</div>
                </div>
                {u.is_owner ? (
                  <span className="text-xs text-[#94A3B8]">Protégé</span>
                ) : (
                  <button onClick={() => { setDelUserErr(""); setDelUser(u); }} className="btn-remove !py-1 !px-2.5 text-xs" data-testid={`sa-user-delete-${u.id}`}><Trash2 className="w-3.5 h-3.5" /> Supprimer</button>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!delUser} title="Supprimer l'utilisateur"
        message={`Supprimer définitivement « ${delUser?.name || delUser?.email} » ? Son accès et ses jetons de connexion seront supprimés. L'historique des ventes et mouvements de l'entreprise est conservé.`}
        confirmLabel="Supprimer" loading={delUserLoading} error={delUserErr} onConfirm={doDeleteUser} onClose={() => setDelUser(null)} />

      <ConfirmDialog open={!!delTenant} title="Supprimer l'entreprise"
        message={`Supprimer définitivement « ${delTenant?.name} » et TOUTES ses données (utilisateurs, activités, ventes, stock…) ? Cette action est irréversible.`}
        confirmLabel="Supprimer définitivement" loading={delTenantLoading} error={delTenantErr} onConfirm={doDeleteTenant} onClose={() => setDelTenant(null)} />
    </div>
  );
}
